/** @jsxImportSource @emotion/react */
import { useState } from 'react';

import { type ApiPropertyProject, projectLifecycleActivityEventTypes } from '@kaiizen/kai-pro-api';
import { useProjectActivityLog, usePropertyActivityLog } from '@kaiizen/kai-pro-api-react-state';

import { DEFAULT_PROPERTY_ACTIVITY_SECTION_STYLES, type PropertyActivitySectionStyles } from './propertyActivitySectionStyles';
import { getPropertyActivityTitle, getStackedDate } from './propertyOverviewLib';

const PAGE_SIZE = 10;

/**
 * What happened at the property, at the level of its jobs: each one created, completed or reopened,
 * archived or brought back, who did it, newest first. What happened inside a job is that job's own
 * activity. Filtering to one job reads that job's own feed, so the list is complete, not just the
 * part of the property's feed loaded so far.
 */
export const PropertyActivitySection = ({
  orgPropertyId,
  projects,
  getProjectTypeLabel,
  onOpenJob,
  styles = DEFAULT_PROPERTY_ACTIVITY_SECTION_STYLES,
}: {
  orgPropertyId: string;
  /** Every job at the property, archived ones included - the filter's choices and each row's job. */
  projects: readonly ApiPropertyProject[];
  getProjectTypeLabel: (projectType?: string | null) => string | null | undefined;
  onOpenJob: (project: ApiPropertyProject) => void;
  styles?: PropertyActivitySectionStyles;
}) => {
  const [jobFilter, setJobFilter] = useState('');
  const options = { limit: PAGE_SIZE, eventTypes: projectLifecycleActivityEventTypes };
  const propertyFeed = usePropertyActivityLog(jobFilter ? undefined : orgPropertyId, options);
  const jobFeed = useProjectActivityLog(jobFilter || undefined, options);
  const feed = jobFilter ? jobFeed : propertyFeed;
  const projectsById = new Map(projects.map(project => [project.id, project]));
  const rows = feed.entries.filter(entry => getPropertyActivityTitle(entry));

  return (
    <section css={styles.shared.section} aria-labelledby='property-activity-title'>
      <div css={styles.shared.sectionHeader}>
        <h2 id='property-activity-title' css={styles.shared.sectionTitle}>Activity</h2>
        <select css={styles.filter} aria-label='Filter activity by job' value={jobFilter} onChange={event => setJobFilter(event.target.value)}>
          <option value=''>All jobs</option>
          {projects.map(project => <option key={project.id} value={project.id}>{project.name || 'Untitled job'}</option>)}
        </select>
      </div>

      <div css={styles.tableWrap}>
        <table css={styles.table} role='table'>
          <thead role='rowgroup'>
            <tr role='row'>
              <th css={styles.headerCell} role='columnheader' aria-label='When' />
              <th css={[styles.headerCell, styles.spacerCell]} role='presentation' />
              <th css={styles.headerCell} role='columnheader'>Who</th>
              <th css={styles.headerCell} role='columnheader'>Job</th>
              <th css={styles.headerCell} role='columnheader'>Type</th>
              <th css={[styles.headerCell, styles.whatCell]} role='columnheader'>What</th>
            </tr>
          </thead>
          <tbody role='rowgroup'>
            {rows.length === 0
              ? (
                <tr role='row'>
                  <td css={styles.empty} role='cell'>{feed.isLoading ? 'Loading activity…' : 'Nothing has happened here yet.'}</td>
                </tr>
              )
              : rows.map((entry, index) => {
                const project = entry.projectId ? projectsById.get(entry.projectId) : undefined;
                const when = getStackedDate(entry.createdAt);
                const marker = styles.markerByEvent[entry.eventType as keyof typeof styles.markerByEvent];
                return (
                  <tr key={entry.id} css={styles.row} role='row' data-testid='property-activity'>
                    <td css={[styles.cell, styles.dateCell]} role='cell'>
                      <span css={styles.dateDay}>{when?.day}</span>
                      <span css={styles.dateYear}>{when?.year}</span>
                    </td>
                    <td css={[styles.railCell, index === 0 && styles.firstRailCell]} role='presentation' aria-hidden='true'>
                      <span css={[styles.marker, marker]} />
                    </td>
                    <td css={styles.cell} role='cell'>{entry.actorName ?? <span css={styles.shared.dash}>—</span>}</td>
                    <td css={styles.cell} role='cell'>
                      {project
                        ? <button type='button' css={styles.jobName} onClick={() => onOpenJob(project)}>{project.name || 'Untitled job'}</button>
                        : <span css={styles.shared.dash}>—</span>}
                    </td>
                    <td css={[styles.cell, styles.secondaryText]} role='cell'>
                      {getProjectTypeLabel(project?.customerMetadata?.projectType) ?? <span css={styles.shared.dash}>—</span>}
                    </td>
                    <td css={[styles.cell, styles.whatCell]} role='cell'>{getPropertyActivityTitle(entry)}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      {feed.hasMore
        ? (
          <button type='button' css={styles.more} disabled={feed.isLoadingMore} onClick={() => void feed.loadMore()}>
            {feed.isLoadingMore ? 'Loading…' : 'Show earlier activity'}
          </button>
        )
        : null}
    </section>
  );
};
